/**
 * FactionSystem.ts — Faction reputation tracking and standing calculation.
 *
 * 3 factions with reputation -100 to +100, standing tiers, and titles.
 */

import type { GameState } from '@core/engine/GameState';
import type { EventSystem } from '@core/engine/EventSystem';
import {
  getAllFactions,
  getStanding,
  getFactionTitle,
  type FactionId,
  type FactionStanding,
} from '@game/data/factions';

// ─── Types ──────────────────────────────────────────────────────

export interface FactionStatus {
  factionId: FactionId;
  name: string;
  reputation: number;
  standing: FactionStanding;
  title?: string;
  symbol: string;
  color: 'frostBlue' | 'green' | 'magenta';
}

// ─── FactionSystem ──────────────────────────────────────────────

export class FactionSystem {
  private events: EventSystem;

  constructor(events: EventSystem) {
    this.events = events;
  }

  /**
   * Modify faction reputation. Clamped to -100..100.
   */
  modifyReputation(factionId: FactionId, amount: number, state: GameState): void {
    const current = state.factionReputation.get(factionId) ?? 0;
    const oldStanding = getStanding(current);
    const newRep = Math.max(-100, Math.min(100, current + amount));
    state.factionReputation.set(factionId, newRep);

    const newStanding = getStanding(newRep);

    this.events.emit('factionReputationChanged', {
      factionId,
      oldReputation: current,
      newReputation: newRep,
      change: amount,
      oldStanding,
      newStanding,
    });

    if (oldStanding !== newStanding) {
      this.events.emit('factionStandingChanged', {
        factionId,
        oldStanding,
        newStanding,
        reputation: newRep,
      });
    }
  }

  /**
   * Get the current reputation value for a faction.
   */
  getReputation(factionId: FactionId, state: GameState): number {
    return state.factionReputation.get(factionId) ?? 0;
  }

  /**
   * Get standing tier for a faction.
   */
  getStanding(factionId: FactionId, state: GameState): FactionStanding {
    const rep = this.getReputation(factionId, state);
    return getStanding(rep);
  }

  /**
   * Get all faction statuses for display.
   */
  getAllFactionStatuses(state: GameState): FactionStatus[] {
    return getAllFactions().map(faction => {
      const rep = this.getReputation(faction.id, state);
      const standing = getStanding(rep);
      const title = getFactionTitle(faction.id, standing);
      return {
        factionId: faction.id,
        name: faction.name,
        reputation: rep,
        standing,
        title,
        symbol: faction.symbol,
        color: faction.color,
      };
    });
  }

  /**
   * Check if a player has at least the given standing with a faction.
   */
  hasMinStanding(factionId: FactionId, minStanding: FactionStanding, state: GameState): boolean {
    const standingOrder: FactionStanding[] = ['Hostile', 'Unfriendly', 'Neutral', 'Friendly', 'Revered'];
    const current = this.getStanding(factionId, state);
    return standingOrder.indexOf(current) >= standingOrder.indexOf(minStanding);
  }

  /**
   * Initialize faction reputations with class affinity bonuses.
   */
  initializeForClass(state: GameState): void {
    for (const faction of getAllFactions()) {
      const affinity = faction.classAffinity?.[state.character.classId] ?? 0;
      if (affinity !== 0) {
        state.factionReputation.set(faction.id, affinity);
      }
    }
  }
}
